import React, { useState, useEffect } from 'react';
import { 
  PlusIcon, 
  PencilIcon, 
  TrashIcon, 
  CubeIcon,
  ExclamationTriangleIcon
  // CheckCircleIcon // Unused - removed
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';

const VesselInventoryTab = ({ vessel, onVesselUpdate, user }) => {
  const [inventory, setInventory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    category: 'parts',
    part_number: '',
    manufacturer: '',
    quantity: '',
    min_quantity: '',
    location: '',
    condition: 'new',
    purchase_date: '',
    purchase_price: '',
    supplier: '',
    notes: ''
  });

  const categories = [
    { value: 'parts', label: 'Parts' },
    { value: 'supplies', label: 'Supplies' },
    { value: 'tools', label: 'Tools' },
    { value: 'safety', label: 'Safety Equipment' },
    { value: 'electrical', label: 'Electrical' },
    { value: 'plumbing', label: 'Plumbing' },
    { value: 'engine', label: 'Engine Parts' },
    { value: 'other', label: 'Other' }
  ];

  const conditions = [
    { value: 'new', label: 'New' },
    { value: 'good', label: 'Good' },
    { value: 'fair', label: 'Fair' },
    { value: 'poor', label: 'Poor' },
    { value: 'damaged', label: 'Damaged' }
  ];

  useEffect(() => {
    loadInventory();
  }, [vessel?.id]);

  const loadInventory = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`/api/vessels/${vessel.id}/inventory`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (!response.ok) {
        throw new Error('Failed to fetch inventory');
      }
      
      const data = await response.json();
      setInventory(data.items || []);
    } catch (error) {
      console.error('Error loading inventory:', error);
      toast.error('Failed to load inventory');
    } finally {
      setLoading(false);
    }
  };

  const handleAddItem = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`/api/vessels/${vessel.id}/inventory`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to add inventory item');
      }

      toast.success('Inventory item added successfully');
      setShowAddModal(false);
      resetForm();
      loadInventory();
    } catch (error) {
      console.error('Error adding inventory item:', error);
      toast.error(error.message || 'Failed to add inventory item');
    }
  };

  const handleEditItem = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`/api/vessels/${vessel.id}/inventory/${selectedItem.id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to update inventory item');
      }

      toast.success('Inventory item updated successfully');
      setShowEditModal(false);
      setSelectedItem(null);
      loadInventory();
    } catch (error) {
      console.error('Error updating inventory item:', error);
      toast.error(error.message || 'Failed to update inventory item');
    }
  };

  const handleDeleteItem = async (itemId) => {
    if (!window.confirm('Are you sure you want to delete this inventory item?')) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`/api/vessels/${vessel.id}/inventory/${itemId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to delete inventory item');
      }

      toast.success('Inventory item deleted successfully');
      loadInventory();
    } catch (error) {
      console.error('Error deleting inventory item:', error);
      toast.error(error.message || 'Failed to delete inventory item');
    }
  };

  const openEditModal = (item) => {
    setSelectedItem(item);
    setFormData({
      name: item.name || '',
      category: item.category || 'parts',
      part_number: item.part_number || '',
      manufacturer: item.manufacturer || '',
      quantity: item.quantity || '',
      min_quantity: item.min_quantity || '',
      location: item.location || '',
      condition: item.condition || 'new',
      purchase_date: item.purchase_date || '',
      purchase_price: item.purchase_price || '',
      supplier: item.supplier || '',
      notes: item.notes || ''
    });
    setShowEditModal(true);
  };

  const resetForm = () => {
    setFormData({
      name: '',
      category: 'parts',
      part_number: '',
      manufacturer: '',
      quantity: '',
      min_quantity: '',
      location: '',
      condition: 'new',
      purchase_date: '',
      purchase_price: '',
      supplier: '',
      notes: ''
    });
  };

  const getConditionColor = (condition) => {
    switch (condition) {
      case 'new': return 'bg-marine-100 text-marine-800';
      case 'good': return 'bg-marine-50 text-marine-700';
      case 'fair': return 'bg-marine-100 text-marine-600';
      case 'poor': return 'bg-marine-50 text-marine-500';
      case 'damaged': return 'bg-gray-100 text-gray-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const isLowStock = (item) => {
    const quantity = parseInt(item.quantity) || 0;
    const minQuantity = parseInt(item.min_quantity) || 0;
    return quantity <= minQuantity && minQuantity > 0;
  };

  const getCategoryIcon = (category) => {
    switch (category) {
      case 'parts': return '🔧';
      case 'supplies': return '📦';
      case 'tools': return '🛠️';
      case 'safety': return '🦺';
      case 'electrical': return '⚡';
      case 'plumbing': return '🚿';
      case 'engine': return '🔩';
      default: return '📦';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <div className="loading-spinner mx-auto mb-4"></div>
          <p className="text-gray-600">Loading inventory...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold text-gray-900">Inventory Management</h2>
        <button
          onClick={() => setShowAddModal(true)}
          className="btn btn-primary flex items-center"
        >
          <PlusIcon className="h-4 w-4 mr-2" />
          Add Inventory Item
        </button>
      </div>

      {/* Low Stock Alerts */}
      {inventory.filter(isLowStock).length > 0 && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-md p-4">
          <div className="flex">
            <ExclamationTriangleIcon className="h-5 w-5 text-yellow-400" />
            <div className="ml-3">
              <h3 className="text-sm font-medium text-yellow-800">
                Low Stock Alerts
              </h3>
              <div className="mt-2 text-sm text-yellow-700">
                <ul className="list-disc list-inside space-y-1">
                  {inventory.filter(isLowStock).map(item => (
                    <li key={item.id}>
                      {item.name} - {item.quantity} remaining (min: {item.min_quantity})
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Inventory List */}
      {inventory.length === 0 ? (
        <div className="text-center py-12">
          <CubeIcon className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">No inventory items</h3>
          <p className="mt-1 text-sm text-gray-500">Get started by adding an inventory item.</p>
          <div className="mt-6">
            <button
              onClick={() => setShowAddModal(true)}
              className="btn btn-primary"
            >
              <PlusIcon className="h-4 w-4 mr-2" />
              Add Inventory Item
            </button>
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {inventory.map((item) => (
            <div key={item.id} className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <div className="flex items-start justify-between">
                <div className="flex items-center space-x-3">
                  <div className="text-2xl">{getCategoryIcon(item.category)}</div>
                  <div className="min-w-0 flex-1">
                    <h3 className="text-lg font-medium text-gray-900">{item.name}</h3>
                    <p className="text-sm text-gray-500">{item.manufacturer} {item.part_number}</p>
                    <div className="flex items-center space-x-2 mt-1">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getConditionColor(item.condition)}`}>
                        {item.condition.toUpperCase()}
                      </span>
                      {isLowStock(item) && (
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-marine-100 text-marine-800">
                          LOW STOCK
                        </span>
                      )}
                    </div>
                  </div>
                </div>
                <div className="flex space-x-1">
                  <button
                    onClick={() => openEditModal(item)}
                    className="p-1 text-gray-400 hover:text-gray-600"
                  >
                    <PencilIcon className="h-4 w-4" />
                  </button>
                  <button
                    onClick={() => handleDeleteItem(item.id)}
                    className="p-1 text-gray-400 hover:text-marine-600"
                  >
                    <TrashIcon className="h-4 w-4" />
                  </button>
                </div>
              </div>

              <div className="mt-4 space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Quantity:</span>
                  <span className="font-medium">{item.quantity}</span>
                </div>
                {item.min_quantity && (
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Min Quantity:</span>
                    <span className="font-medium">{item.min_quantity}</span>
                  </div>
                )}
                {item.location && (
                  <div className="text-sm text-gray-600">
                    <strong>Location:</strong> {item.location}
                  </div>
                )}
                {item.supplier && (
                  <div className="text-sm text-gray-600">
                    <strong>Supplier:</strong> {item.supplier}
                  </div>
                )}
                {item.purchase_price && (
                  <div className="text-sm text-gray-600">
                    <strong>Price:</strong> ${item.purchase_price}
                  </div>
                )}
              </div>

              {item.notes && (
                <div className="mt-4">
                  <p className="text-sm text-gray-600">{item.notes}</p>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Add/Edit Modal */}
      {(showAddModal || showEditModal) && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-10 mx-auto p-5 border w-full max-w-2xl shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                {selectedItem ? 'Edit Inventory Item' : 'Add Inventory Item'}
              </h3>
              <form onSubmit={selectedItem ? handleEditItem : handleAddItem} className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Name *</label>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={(e) => setFormData({...formData, [e.target.name]: e.target.value})}
                      required
                      className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-marine-500 focus:border-marine-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Category *</label>
                    <select
                      name="category"
                      value={formData.category}
                      onChange={(e) => setFormData({...formData, [e.target.name]: e.target.value})}
                      required
                      className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-marine-500 focus:border-marine-500"
                    >
                      {categories.map(category => (
                        <option key={category.value} value={category.value}>{category.label}</option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Part Number</label>
                    <input
                      type="text"
                      name="part_number"
                      value={formData.part_number}
                      onChange={(e) => setFormData({...formData, [e.target.name]: e.target.value})}
                      className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-marine-500 focus:border-marine-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Manufacturer</label>
                    <input
                      type="text"
                      name="manufacturer"
                      value={formData.manufacturer}
                      onChange={(e) => setFormData({...formData, [e.target.name]: e.target.value})}
                      className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-marine-500 focus:border-marine-500"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Quantity *</label>
                    <input
                      type="number"
                      name="quantity"
                      value={formData.quantity}
                      onChange={(e) => setFormData({...formData, [e.target.name]: e.target.value})}
                      required
                      min="0"
                      className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-marine-500 focus:border-marine-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Min Quantity</label>
                    <input
                      type="number"
                      name="min_quantity"
                      value={formData.min_quantity}
                      onChange={(e) => setFormData({...formData, [e.target.name]: e.target.value})}
                      min="0"
                      className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-marine-500 focus:border-marine-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Condition</label>
                    <select
                      name="condition"
                      value={formData.condition}
                      onChange={(e) => setFormData({...formData, [e.target.name]: e.target.value})}
                      className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-marine-500 focus:border-marine-500"
                    >
                      {conditions.map(condition => (
                        <option key={condition.value} value={condition.value}>{condition.label}</option>
                      ))}
                    </select>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">Location</label>
                  <input
                    type="text"
                    name="location"
                    value={formData.location}
                    onChange={(e) => setFormData({...formData, [e.target.name]: e.target.value})}
                    className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-marine-500 focus:border-marine-500"
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Purchase Date</label>
                    <input
                      type="date"
                      name="purchase_date"
                      value={formData.purchase_date}
                      onChange={(e) => setFormData({...formData, [e.target.name]: e.target.value})}
                      className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-marine-500 focus:border-marine-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Purchase Price</label>
                    <input
                      type="number"
                      name="purchase_price"
                      value={formData.purchase_price}
                      onChange={(e) => setFormData({...formData, [e.target.name]: e.target.value})}
                      step="0.01"
                      min="0"
                      className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-marine-500 focus:border-marine-500"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">Supplier</label>
                  <input
                    type="text"
                    name="supplier"
                    value={formData.supplier}
                    onChange={(e) => setFormData({...formData, [e.target.name]: e.target.value})}
                    className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-marine-500 focus:border-marine-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">Notes</label>
                  <textarea
                    name="notes"
                    value={formData.notes}
                    onChange={(e) => setFormData({...formData, [e.target.name]: e.target.value})}
                    rows="3"
                    className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-marine-500 focus:border-marine-500"
                  />
                </div>

                <div className="flex justify-end space-x-3 pt-4">
                  <button
                    type="button"
                    onClick={() => {
                      setShowAddModal(false);
                      setShowEditModal(false);
                      setSelectedItem(null);
                    }}
                    className="btn btn-secondary"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="btn btn-primary"
                  >
                    {selectedItem ? 'Update' : 'Add'} Item
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default VesselInventoryTab;
